import {
  LitElement,
  html,
  css,
  unsafeHTML,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

const EVENTS = {
  PRODUCT_CLICKED: "bundle-builder:product-clicked",
  SECTION_UPDATED: "bundle-builder:section-updated",
  BOX_LOADED: "bundle-builder:box-loaded",
  CART_SYNCED: "bundle-builder:cart-synced",
};

// GA4 funnel events. window.ffTrack (assets/ff-analytics.js) forwards them to
// the Google channel's gtag; a no-op when the helper is missing.
const track = (name, params) => {
  if (typeof window.ffTrack === "function") window.ffTrack(name, params);
};
const clip = (value, max = 100) =>
  typeof window.ffTrack === "function" ? window.ffTrack.clip(value, max) : value;
const currencyCode = () => window.Shopify?.currency?.active || "USD";

// Klaviyo metric for "Add All to Cart", for the bundle abandonment flow: Klaviyo's
// own Added to Cart sends one event per product and drops the line properties
// (docs/quiz-bundle-klaviyo.md). klaviyo.js sends it for whoever it has
// identified; a no-op otherwise.
const KLAVIYO_BUNDLE_EVENT = "Added Bundle Builder Box";
const money = (value) => `$${value.toFixed(2)}`;
const roundCents = (value) => Math.round(value * 100) / 100;

/**
 * Bundle Builder Component
 * @docs Skio: https://integrate.skio.com/
 * @author Kobi Umarbekov, Avex Team
 * @description Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */
export class BundleBuilderComponent extends LitElement {
  static properties = {
    productPlatformId: {
      type: String,
      attribute: "product-platform-id",
    },
    dynamicBox: {
      type: Object,
      state: true,
    },
    moneyFormat: {
      type: String,
    },
    $sections: {
      type: Array,
      state: true,
    },
  };

  constructor() {
    super();
    this.$sections = [];
  }

  connectedCallback() {
    super.connectedCallback();
    console.log("[Bundle Builder Component]: Connected Callback");
    this.fetchBox();
    this.$sections = [...this.querySelectorAll("bundle-builder-section")];
  }

  // Fired after all children are rendered
  firstUpdated() {
    console.log(
      "[Bundle Builder Component]: Sections found",
      this.$sections.length
    );
    this.setupSynchronizedScrolling();
  }

  render() {
    return html`<slot></slot>`;
  }

  updated(changed) {
    if (changed.has("dynamicBox") && this.dynamicBox) {
      console.log("[Bundle Builder Component]: Dynamic Box", this.dynamicBox);
      // Assign the SAME box data to ALL sections
      this.$sections.forEach((section, index) => {
        section.box = this.dynamicBox;
        section.sectionIndex = index;
      });
      track("bundle_view", {
        box_id: clip(this.dynamicBox.boxId, 60),
        step_count: this.$sections.length,
      });
      // Notify cart component that box data is ready
      this.dispatchEvent(
        new CustomEvent(EVENTS.BOX_LOADED, {
          detail: { box: this.dynamicBox },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  async fetchBox() {
    if (!this.productPlatformId) {
      console.error("[Bundle Builder Component]: Product Platform ID is required");
      return;
    }

    const response = await fetch(
      "https://api.skio.com/storefront-http/get-dynamic-box",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productPlatformId: this.productPlatformId,
        }),
      }
    );

    const data = await response.json();
    // API returns { ok: true, DynamicBox: { ...actual box data... } }
    this.dynamicBox = data.DynamicBox || data;
    console.log("[Bundle Builder Component]: Fetched Dynamic Box", this.dynamicBox);
  }

  setupSynchronizedScrolling() {
    // Wait for cart component to be available
    setTimeout(() => {
      const cartComponent = this.querySelector("bundle-builder-cart");
      if (!cartComponent) return;

      // Find cart sections container
      const cartSectionsContainer = cartComponent.shadowRoot?.querySelector(".cart__sections");
      if (!cartSectionsContainer) return;

      // Create intersection observer to watch main sections
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Find the index of the currently visible section
            const sectionIndex = this.$sections.indexOf(entry.target);
            if (sectionIndex >= 0) {
              // Scroll corresponding cart section into view
              const cartSections = cartSectionsContainer.querySelectorAll('.cart-section');
              if (cartSections[sectionIndex]) {
                cartSections[sectionIndex].scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'start'
                });
              }
            }
          }
        });
      }, {
        threshold: [0, 0.5, 1],
        rootMargin: '-20% 0px -20% 0px'
      });

      // Observe all main sections
      this.$sections.forEach(section => {
        observer.observe(section);
      });

      // Store observer for cleanup
      this.scrollObserver = observer;
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/;

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) {
      if (isNaN(number) || number == null) {
        return "0";
      }

      const numString = (number / 100.0).toFixed(precision);
      const parts = numString.split(".");
      const dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      );
      const cents = parts[1] ? decimal + parts[1] : "";
      return dollars + cents;
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case "amount":
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2);
        case "amount_no_decimals":
          // Ex. 1,135
          return formatWithDelimiters(cents, 0);
        case "amount_with_comma_separator":
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, ".", ",");
        case "amount_with_apostrophe_separator":
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", ".");
        case "amount_no_decimals_with_space_separator":
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, " ");
        case "amount_with_space_separator":
          // 1 134,65
          return formatWithDelimiters(cents, 2, " ", ",");
        case "amount_with_period_and_space_separator":
          // 1 134.65
          return formatWithDelimiters(cents, 2, " ", ".");
        default:
          return match;
      }
    });
  }
}

if (!customElements.get("bundle-builder")) {
  customElements.define("bundle-builder", BundleBuilderComponent);
}

/*
    Bundle Builder Section Component
*/
export class BundleBuilderSectionComponent extends LitElement {
  static properties = {
    sectionIndex: {
      type: Number,
      state: true,
    },
    cartTitle: {
      type: String,
      attribute: "cart-title",
    },
    box: {
      type: Object,
      state: true,
    },
    selections: {
      type: Array,
      state: true,
    },
    valid: {
      type: Boolean,
      reflect: true,
    },
    $products: {
      type: Array,
      state: true,
    },
  };

  constructor() {
    super();
    this.valid = false;
    this.selections = [];
    this.$products = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      EVENTS.PRODUCT_CLICKED,
      this.handleProductSelectionUpdate
    );
    this.$products = [...this.querySelectorAll("bundle-builder-product")];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      EVENTS.PRODUCT_CLICKED,
      this.handleProductSelectionUpdate
    );
  }

  render() {
    return html`<slot></slot>`;
  }

  updated(changed) {
    if (changed.has("selections") && this.box) {
      // Section is valid if exactly 1 product is selected
      this.valid = this.isSelectionsValid(this.selections);
      this.validateProducts();
      this.dispatchEvent(
        new CustomEvent(EVENTS.SECTION_UPDATED, {
          detail: {
            sectionIndex: this.sectionIndex,
            selections: this.selections,
          },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  handleProductSelectionUpdate(event) {
    this.trackSelection(event);

    // Deep copy the selections array to avoid mutating the original array
    let selections = JSON.parse(JSON.stringify(this.selections));

    if (!event.detail.quantity) {
      selections = selections.filter(
        ({ variantId }) => variantId !== event.detail.variantId
      );
    } else {
      const selection = selections.find(
        ({ variantId }) => variantId === event.detail.variantId
      );
      if (selection) {
        selection.quantity = event.detail.quantity;
      } else {
        selections.push({
          variantId: event.detail.variantId,
          quantity: event.detail.quantity,
        });
      }
    }

    // TODO: We currently take only one element from each section - change this logic if needed
    this.selections =
      selections.length > 0 ? [selections[selections.length - 1]] : [];
  }

  // bundle_step_select / bundle_step_remove with the step and product names.
  // Product cards are rendered by Liquid, so the title is read from the card.
  trackSelection(event) {
    const { variantId, quantity } = event.detail || {};
    const card = this.$products.find((p) => p.variantId === variantId);
    const title = card?.querySelector(".ff-card__title")?.textContent;
    const replaced = quantity && this.selections.length > 0;
    track(quantity ? "bundle_step_select" : "bundle_step_remove", {
      step_index: (this.sectionIndex ?? 0) + 1,
      step_name: clip(this.cartTitle, 60),
      item_name: clip(title, 100),
      item_variant_id: clip(variantId, 30),
      replaced_selection: replaced ? "yes" : "no",
    });
  }

  isSelectionsValid(selections) {
    // Each section must have exactly 1 product selected
    return selections.length === 1 && selections[0]?.quantity === 1;
  }

  validateProducts() {
    this.$products.forEach((productCard) => {
      const selection = this.selections.find(
        (selection) => selection.variantId === productCard.variantId
      );
      if (selection) {
        productCard.selected = true;
      } else {
        productCard.selected = false;
      }
    });
  }
}

if (!customElements.get("bundle-builder-section")) {
  customElements.define(
    "bundle-builder-section",
    BundleBuilderSectionComponent
  );
}

/*
    Bundle Builder Product Component
*/
export class BundleBuilderProductComponent extends LitElement {
  static styles = [
    css`
      :host {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .card {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      /* Card body is inert: only the Add to Bundle button is interactive. */
      .content {
        pointer-events: none;
        flex: 1 1 auto;
      }

      .sold-out-badge {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 100;
        background-color: #8E847A;
        color: #fff;
        padding: 4px 8px;
        font-family: "Chalet", sans-serif;
        font-size: 12px;
        text-transform: uppercase;
        font-weight: 600;
        line-height: normal;
        font-style: italic;
      }

      .add-button {
        display: block;
        width: 100%;
        margin-top: 12px;
        padding: 11px 12px;
        background: #fff;
        color: #000;
        border: 1px solid #000;
        border-radius: 0;
        font-family: "Chalet", sans-serif;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.13em;
        text-transform: uppercase;
        cursor: pointer;
        transition: background-color 0.25s ease, color 0.25s ease,
          border-color 0.25s ease;
      }

      .add-button:hover {
        background: #000;
        color: #fff;
      }

      .add-button[data-selected] {
        background: #8c8379;
        color: #fff;
        border-color: #8c8379;
      }

      .add-button[data-selected]:hover {
        background: #6d6560;
        border-color: #6d6560;
      }

      .add-button:disabled {
        background: #f6f6f6;
        color: #8c8379;
        border-color: #e3d7cb;
        cursor: not-allowed;
      }

      /* No focus outline at all. The accessibility widget forces :focus-visible
         even on mouse clicks (so a keyboard-only ring can't be isolated) and
         injects a blue outline into shadow roots; !important suppresses it.
         The selected state is conveyed by the button's color change instead. */
      .add-button:focus,
      .add-button:focus-visible {
        outline: none !important;
      }

      .add-button .label-remove {
        display: none;
      }

      .add-button[data-selected]:hover .label-added {
        display: none;
      }

      .add-button[data-selected]:hover .label-remove {
        display: inline;
      }
    `,
  ];

  static properties = {
    selected: {
      type: Boolean,
      reflect: true,
    },
    variantId: {
      type: String,
      attribute: "variant-id",
    },
    quantity: {
      type: Number,
      reflect: true,
    },
    unavailable: {
      type: Boolean,
      reflect: true,
    },
  };

  constructor() {
    super();
    this.selected = false;
  }

  connectedCallback() {
    super.connectedCallback();
    console.log(
      "[Bundle Builder Product Component]: Connected Callback",
      this.variantId
    );
  }

  render() {
    return html`
      <div class="card">
        ${this.unavailable
          ? html`<div class="sold-out-badge"><slot name="sold-out-badge"></slot></div>`
          : ""}
        <div class="content">
          <slot></slot>
        </div>
        <button
          class="add-button"
          ?disabled=${this.unavailable}
          ?data-selected=${this.selected}
          @click=${this.toggle}
        >
          ${this.unavailable
            ? html`<span>Sold out</span>`
            : this.selected
            ? html`<span class="label-added">Added</span
                ><span class="label-remove">Remove</span>`
            : html`<span>Add to Bundle</span>`}
        </button>
      </div>
    `;
  }

  updated(changed) {
    if (changed.has("selected") && !this.selected) {
      this.quantity = 0;
    }
  }

  toggle() {
    // Prevent interaction if unavailable
    if (this.unavailable) {
      console.log("[Bundle Builder Product Component]: Product unavailable, click ignored", this.variantId);
      return;
    }

    console.log("[Bundle Builder Product Component]: Clicked", this.variantId);

    // TODO: Handle quantity handling if requested
    this.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_CLICKED, {
        detail: {
          variantId: this.variantId,
          quantity: this.selected ? 0 : ++this.quantity,
        },
        bubbles: true,
        composed: true,
      })
    );
    this.selected = !this.selected;
    console.log("[Bundle Builder Product Component]: Selected?", this.selected);
  }
}

if (!customElements.get("bundle-builder-product")) {
  customElements.define(
    "bundle-builder-product",
    BundleBuilderProductComponent
  );
}

/*
    Bundle Builder Cart Component
*/
export class BundleBuilderCartComponent extends LitElement {
  static styles = [
    css`
      *,
      *:after,
      *:before {
        box-sizing: border-box;
      }

      h2,
      h3,
      h4 {
        margin: 0;
        font-family: "Chalet", sans-serif;
        font-weight: 600;
        line-height: 1.2;
        margin-bottom: 0.5em;
      }

      .cart {
        padding: 1.25rem;
        border: 1px solid #e3d7cb;
        text-align: center;
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - var(--nav-height, 80px) - 32px);
      }

      .cart__title {
        font-size: 18px;
        letter-spacing: 0.02em;
      }

      .cart__title-icon {
        display: none;
      }

      /* Slots area is the only scrollable region so the footer (totals +
         checkout) stays pinned and visible within the viewport. */
      .cart__slots {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 16px;
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
      }

      .cart__footer {
        flex-shrink: 0;
      }

      .completion {
        text-align: left;
        margin-bottom: 20px;
      }

      .completion__label {
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #000;
        margin-bottom: 8px;
        line-height: 1.3;
        white-space: nowrap;
      }

      .completion[complete] .completion__label {
        color: #8c8379;
      }

      .completion__track {
        display: flex;
        gap: 6px;
      }

      .completion__step {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }

      .completion__seg {
        height: 4px;
        background: #e3d7cb;
        transition: background-color 0.3s ease;
      }

      .completion__step[filled] .completion__seg {
        background: #8c8379;
      }

      .completion__step-label {
        display: none;
        margin-top: 6px;
        font-size: 12px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: #9a9a9a;
        line-height: 1.2;
        text-align: center;
      }

      /* Step labels show only in the mobile toggler bar; the desktop panel has
         the labeled slots directly below its progress bar. */
      .completion--toggler .completion__step-label {
        display: block;
      }

      .completion__step[filled] .completion__step-label {
        color: #8c8379;
      }

      .cart-slot {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px;
        border: 1px solid #e3d7cb;
        background: #fff;
        text-align: left;
      }

      .cart-slot[filled] {
        background: #f6f6f6;
      }

      .cart-slot__media {
        width: 46px;
        height: 46px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border: 1px solid #ece8e0;
      }

      .cart-slot__media img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      .cart-slot__placeholder {
        font-family: "Chalet", sans-serif;
        font-size: 16px;
        font-weight: 600;
        color: #c9bdae;
      }

      .cart-slot__info {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .cart-slot__step {
        font-size: 12px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #8c8379;
        line-height: 1.2;
      }

      /* In the empty state the step label is redundant with the "Choose your X"
         text, so hide it; keep it on filled slots to label the product. */
      .cart-slot:not([filled]) .cart-slot__step {
        display: none;
      }

      .cart-slot__name {
        font-size: 13px;
        line-height: 1.2;
        color: #000;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cart-slot:not([filled]) .cart-slot__name {
        color: #9a9a9a;
      }

      .cart-slot__price {
        font-size: 13px;
        color: #000;
        flex-shrink: 0;
      }

      .cart-slot__remove {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        margin: 0;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        line-height: 0;
        color: #8c8379;
      }

      /* Suppress the accessibility widget's injected blue focus outline on the
         cart's interactive elements (same fix as the product Add button). */
      .cart-toggler:focus,
      .cart-toggler:focus-visible,
      .cart__checkout:focus,
      .cart__checkout:focus-visible,
      .cart-slot__remove:focus,
      .cart-slot__remove:focus-visible {
        outline: none !important;
      }

      .cart__totals {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .cart__totals li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5em;
      }

      .cart__checkout {
        display: block;
        width: 100%;
        cursor: pointer;
        border: 1px solid #8c8379;
        padding: 12px 2rem;
        color: #fff;
        background-color: #8c8379;
        text-transform: uppercase;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.13em;
        transition: background-color 0.25s ease, color 0.25s ease,
          border-color 0.25s ease;
      }

      .cart__checkout:not(:disabled):hover {
        background-color: #fff;
        color: #8c8379;
      }

      .cart__checkout:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      @media screen and (min-width: 768px) {
        .cart-toggler {
          display: none;
        }
      }

      @media screen and (max-width: 767px) {
        .cart-wrapper {
          display: block;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          top: auto;
          width: 100%;
          height: auto;
          background: #fff;
          z-index: 1000;
          text-align: left;
          font-size: 14px;
          border-top: 1px solid #e3d7cb;
          box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
        }

        .cart {
          padding-top: 12px;
          border: 0;
          padding: 0;
          overflow: hidden;
          max-height: 0;
          transition: 0.3s ease;
        }

        .cart-toggler {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px 16px;
          border: 0;
          width: 100%;
          background-color: #fff;
          text-transform: uppercase;
          letter-spacing: 1.12px;
          font-size: 13px;
          font-weight: 700;
          color: #000;
          line-height: 1.4;
          position: relative;
          text-align: left;
        }

        .cart-toggler__row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .cart-toggler__chevron {
          margin-left: auto;
          flex-shrink: 0;
          box-sizing: border-box;
          width: 8px;
          height: 8px;
          border-right: 1.5px solid #000;
          border-bottom: 1.5px solid #000;
          /* Closed: chevron points up (tap to expand the sheet upward). */
          transform: rotate(225deg);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .cart-toggler[open] .cart-toggler__chevron {
          /* Open: chevron points down (tap to collapse). */
          transform: rotate(45deg);
        }

        .cart[open] {
          max-height: 70vh;
          overflow-y: auto;
          padding: 0 16px 16px 16px;
        }

        /* Panel-variant completion bar is shown on desktop only; on mobile the
           always-visible toggler strip carries the progress bar instead. */
        .completion--panel {
          display: none;
        }

        .cart__totals li {
          font-size: 16px;
        }

        .cart__totals li:first-child {
          display: none;
        }

        .cart__title {
          display: none;
        }

        .cart__subtitle {
          font-size: inherit;
          margin-bottom: 12px;
          font-size: 16px;
          text-align: left;
          display: none;
        }

        .cart__checkout {
          padding-block: 16px;
        }

        .cart__slots {
          overflow: visible;
          flex: 0 0 auto;
          margin-bottom: 12px;
        }

        .cart__title-icon {
          transform: rotateZ(180deg);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
      }
    `,
  ];

  static properties = {
    title: {
      type: String,
    },
    $root: {
      type: Object,
      state: true,
    },
    $sections: {
      type: Array,
      state: true,
    },
    $sections: {
      type: Array,
      state: true,
    },
    cartItemsCount: {
      type: Number,
      state: true,
    },
    valid: {
      type: Boolean,
      reflect: true,
    },
    loading: {
      type: Boolean,
      state: false,
    },
    open: {
      type: Boolean,
      state: true,
    },
  };

  constructor() {
    super();
    this.cartItemsCount = 0;
    this.valid = false;
    this.previousCartItemsCount = 0;
    this.autoCloseTimeout = null;
    this._isSyncingFromCart = false;
    this._cartSyncDebounceTimeout = null;
  }

  // Number of steps (categories) that currently have a valid selection.
  get completedSteps() {
    const sections = this.$root?.$sections || [];
    return sections.filter((section) => section.valid).length;
  }

  get totalSteps() {
    return this.$root?.$sections?.length || 0;
  }

  // Highest discount percentage from the Skio box (e.g. "20%"), with a fallback.
  get discountLabel() {
    const tiers = this.$root?.dynamicBox?.percentDiscountTier || {};
    const max = Object.values(tiers)
      .map((value) => parseFloat(value))
      .filter((value) => !isNaN(value))
      .sort((a, b) => b - a)[0];
    return max ? `${Math.round(max)}%` : "20%";
  }

  getCompletionCopy(totals) {
    if (!this.$root?.dynamicBox) return "";
    const completed = this.completedSteps;
    const total = this.totalSteps || 3;
    if (this.valid && completed >= total) {
      return `Complete! You saved ${this.$root.money(totals.savings)}`;
    }
    const remaining = total - completed;
    return `${completed} of ${total} - add ${remaining} more to unlock ${this.discountLabel} off`;
  }

  // 3-step completion bar. `variant` ("panel" | "toggler") controls which
  // breakpoint shows it (desktop panel vs. mobile bottom-bar strip).
  renderCompletion(totals, variant) {
    const sections = this.$root?.$sections || [];
    const completed = this.completedSteps;
    const total = this.totalSteps || 3;
    const complete = this.valid && completed >= total;
    const copy = this.getCompletionCopy(totals);
    const steps = [];
    for (let i = 0; i < total; i++) {
      const filled = Boolean(sections[i]?.valid);
      steps.push(html`
        <div class="completion__step" ?filled=${filled}>
          <div class="completion__seg"></div>
          <span class="completion__step-label"
            >${sections[i]?.cartTitle || ""}</span
          >
        </div>
      `);
    }
    return html`
      <div class="completion completion--${variant}" ?complete=${complete}>
        ${copy ? html`<div class="completion__label">${copy}</div>` : ""}
        <div class="completion__track">${steps}</div>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.$root = this.closest("bundle-builder");
    this.$sections = [...this.$root.$sections].map((section) => {
      section.selectionsWithProductVariants =
        this.getSectionSelectedProducts(section);
      return section;
    });
    this.$root.addEventListener(
      EVENTS.SECTION_UPDATED,
      this.fetchState.bind(this)
    );
    // Also refresh when box data loads
    this.$root.addEventListener(
      EVENTS.BOX_LOADED,
      this.fetchState.bind(this)
    );

    // Subscribe to cart state changes for bidirectional sync
    this.setupCartSync();
  }

  setupCartSync() {
    // Check if liquidAjaxCart is available
    if (typeof liquidAjaxCart !== "undefined" && liquidAjaxCart.subscribeToCartStateUpdate) {
      // Store bound function for cleanup
      this._cartSyncHandler = (cartState) => {
        this.syncFromCart(cartState);
      };

      liquidAjaxCart.subscribeToCartStateUpdate(this._cartSyncHandler);
    } else {
      // Retry after a short delay if liquidAjaxCart isn't loaded yet
      setTimeout(() => this.setupCartSync(), 500);
    }
  }

  syncFromCart(cartState) {
    // Skip sync if we're in the middle of adding to cart or already syncing
    if (this.loading || this._isSyncingFromCart) {
      return;
    }

    // Debounce to avoid rapid successive calls
    if (this._cartSyncDebounceTimeout) {
      clearTimeout(this._cartSyncDebounceTimeout);
    }

    this._cartSyncDebounceTimeout = setTimeout(() => {
      this._performCartSync(cartState);
    }, 100);
  }

  _performCartSync(cartState) {
    const dynamicBox = this.$root?.dynamicBox;
    if (!dynamicBox || !cartState?.cart?.items) {
      return;
    }

    // Find all cart items that belong to this bundle (by _dynamicBoxId)
    const bundleItemsInCart = cartState.cart.items.filter(
      (item) => item.properties?._dynamicBoxId === dynamicBox.boxId
    );

    // IMPORTANT: Only sync if the bundle has been added to cart at least once.
    // If there are no bundle items in cart, the user is still building their bundle
    // and we shouldn't remove their selections.
    if (bundleItemsInCart.length === 0) {
      return;
    }

    // Get variant IDs currently in cart for this bundle
    const cartVariantIds = new Set(
      bundleItemsInCart.map((item) => String(item.variant_id))
    );

    console.log("[Bundle Builder Cart Component]: Syncing from cart, bundle items:", cartVariantIds);

    // Check each section's selections and remove any not in cart
    let hasChanges = false;
    this._isSyncingFromCart = true;

    try {
      this.$root.$sections.forEach((section) => {
        // Create a copy of selections to iterate safely
        const selectionsToCheck = [...section.selections];

        selectionsToCheck.forEach((selection) => {
          if (!cartVariantIds.has(String(selection.variantId))) {
            console.log(
              "[Bundle Builder Cart Component]: Removing variant from bundle selection:",
              selection.variantId
            );

            // Find the product card and deselect it
            const $productCard = section.querySelector(
              `bundle-builder-product[variant-id="${selection.variantId}"]`
            );

            if ($productCard && $productCard.selected) {
              // Dispatch event to deselect (same as clicking when selected)
              $productCard.dispatchEvent(
                new CustomEvent(EVENTS.PRODUCT_CLICKED, {
                  detail: {
                    variantId: selection.variantId,
                    quantity: 0,
                  },
                  bubbles: true,
                  composed: true,
                })
              );
              $productCard.selected = false;
              hasChanges = true;
            }
          }
        });
      });

      if (hasChanges) {
        console.log("[Bundle Builder Cart Component]: Cart sync caused changes, dispatching event");
        this.$root.dispatchEvent(
          new CustomEvent(EVENTS.CART_SYNCED, {
            detail: { cartVariantIds: [...cartVariantIds] },
            bubbles: true,
            composed: true,
          })
        );
      }
    } finally {
      this._isSyncingFromCart = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.$root.removeEventListener(
      EVENTS.SECTION_UPDATED,
      this.fetchState.bind(this)
    );
    this.$root.removeEventListener(
      EVENTS.BOX_LOADED,
      this.fetchState.bind(this)
    );

    // Clear any pending auto-close timeout
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }

    // Clean up cart sync resources
    if (this._cartSyncDebounceTimeout) {
      clearTimeout(this._cartSyncDebounceTimeout);
      this._cartSyncDebounceTimeout = null;
    }
    // Note: liquidAjaxCart doesn't provide unsubscribe, but cleanup the handler reference
    this._cartSyncHandler = null;

    // Drop the body flag so a removed cart can't leave widgets hidden.
    document.body.classList.remove("bundle-builder-cart-open");
  }

  async waitForElement(selector, timeout = 5000, interval = 100) {
    const startTime = Date.now();
    
    return new Promise((resolve, reject) => {
      const check = () => {
        const element = document.querySelector(selector);
        
        if (element) {
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          reject(new Error(`Element ${selector} not found within ${timeout}ms`));
        } else {
          setTimeout(check, interval);
        }
      };
      
      check();
    });
  }

  firstUpdated() {
    this.fetchState();
  }

  updated(changedProperties) {
    // Auto-expand the mobile cart only on the transition to a complete bundle
    // (all three categories selected). Stays collapsed for 1–2 items.
    if (
      changedProperties.has("valid") &&
      this.valid &&
      !changedProperties.get("valid") &&
      window.innerWidth < 768
    ) {
      this.open = true;
    }

    // Mirror the open state onto the body so document-level CSS can hide
    // floating widgets (e.g. the accessiBe trigger) while the mobile cart
    // sheet is expanded — same approach the cart drawer uses.
    if (changedProperties.has("open")) {
      document.body.classList.toggle("bundle-builder-cart-open", !!this.open);
    }
  }

  handleToggle() {
    // Clear any auto-close timeout when user manually toggles
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }
    this.open = !this.open;
  }

  render() {
    const totals = this.getTotals();
    return html`
      <div class="cart-wrapper">
        <button
          class="cart-toggler"
          ?open=${this.open}
          @click=${this.handleToggle}
        >
          <div class="cart-toggler__row">
            <span>${this.open ? "Hide" : "See"} your bundle</span>
            <span class="cart-toggler__count">(${this.cartItemsCount})</span>
            <span class="cart-toggler__chevron" aria-hidden="true"></span>
          </div>
          ${this.renderCompletion(totals, "toggler")}
        </button>
        <div class="cart" ?open=${this.open}>
          <h3 class="cart__title" @click=${this.handleToggle}>
            ${this.title.replace("{cart_items_count}", this.cartItemsCount)}
            <div class="cart__title-icon" ?open=${this.open}>
              <slot name="icon-chevron"></slot>
            </div>
          </h3>
          ${this.renderCompletion(totals, "panel")}
          <div class="cart__slots">
            ${this.$sections.map((section, index) => {
              const selection = section.selectionsWithProductVariants?.[0];
              return html`
                <div class="cart-slot" ?filled=${!!selection}>
                  <div class="cart-slot__media">
                    ${selection
                      ? html`<img
                          src="${selection.variant.imageSrc ||
                          selection.product.productImageSrc}"
                          alt="${selection.product.productTitle}"
                        />`
                      : html`<span class="cart-slot__placeholder"
                          >${index + 1}</span
                        >`}
                  </div>
                  <div class="cart-slot__info">
                    <span class="cart-slot__step">${section.cartTitle}</span>
                    <span class="cart-slot__name">
                      ${selection
                        ? selection.product.productTitle
                        : `Choose your ${section.cartTitle.toLowerCase()}`}
                    </span>
                  </div>
                  ${selection
                    ? html`
                        <span class="cart-slot__price"
                          >${this.$root.money(
                            selection.variant.price * 100
                          )}</span
                        >
                        <button
                          class="cart-slot__remove"
                          @click=${() =>
                            this.handleCardRemove({
                              section,
                              variantId: selection.variantId,
                            })}
                          aria-label="Remove ${selection.product.productTitle}"
                        >
                          <slot name="icon-remove-${index}"></slot>
                        </button>
                      `
                    : ""}
                </div>
              `;
            })}
          </div>
          <div class="cart__footer">
            <ul class="cart__totals">
              <li>
                <span>Subtotal (without discount)</span>
                <span>${this.$root.money(totals.totalWithoutDiscount)}</span>
              </li>
              <li>
                <span>Savings</span>
                <span>-${this.$root.money(totals.savings)}</span>
              </li>
              <li>
                <span>Subtotal</span>
                <span>${this.$root.money(totals.total)}</span>
              </li>
            </ul>
            <button
              ?disabled=${!this.valid || this.loading}
              @click=${this.handleCheckout}
              class="cart__checkout"
            >
              ${this.loading ? "Adding to cart..." : "Add All to Cart"}
            </button>
            <slot name="disclaimer"></slot>
          </div>
        </div>
      </div>
    `;
  }

  handleCheckout() {
    if (!this.valid) {
      return;
    }
    console.log("[Bundle Builder Cart Component]: Checkout");
    track("bundle_add_to_cart", this.trackingPayload());

    const dynamicBox = this.$root.dynamicBox;
    // Read before the add: the cart sync afterwards resets the selections.
    const klaviyoProperties = this.klaviyoProperties();

    this.loading = true;
    liquidAjaxCart.cartRequestAdd({
      items: this.$sections.flatMap((section) =>
        section.selectionsWithProductVariants.map(({ variant, quantity }) => ({
          id: variant.platformId.replace("gid://shopify/ProductVariant/", ""),
          quantity,
          properties: {
            _dynamicBoxIndex: 0,
            _dynamicBoxId: dynamicBox.boxId,
          },
        }))
      ),
    }, {
      lastComplete: (result) => {
        document.body.classList.add("js-ajax-cart-open");
        this.loading = false;
        if (result?.responseData?.ok) {
          try {
            window.klaviyo?.track?.(KLAVIYO_BUNDLE_EVENT, klaviyoProperties);
          } catch {
            /* optional */
          }
        }
      },
    });
  }

  // Everything the bundle abandonment email shows: the chosen products, list
  // and discounted prices, and a link back to the builder.
  klaviyoProperties() {
    const totals = this.getTotals();
    const percentOff = totals.percentOff;
    const items = (this.$sections || []).flatMap((section) =>
      (section.selectionsWithProductVariants || []).map(
        ({ product, variant, quantity }) => ({
          ProductName: product.productTitle,
          ProductID: product.productPlatformId?.replace("gid://shopify/Product/", ""),
          VariantID: variant.platformId.replace("gid://shopify/ProductVariant/", ""),
          VariantName: variant.productVariantTitle,
          ProductURL: new URL(`/products/${product.productHandle}`, window.location.origin).href,
          ImageURL: variant.imageSrc || product.productImageSrc || "",
          Quantity: quantity,
          Price: money(variant.price),
          BundlePrice: money(roundCents(variant.price * (1 - percentOff / 100))),
        })
      )
    );
    return {
      Items: items,
      ItemNames: items.map((item) => item.ProductName),
      ItemCount: this.cartItemsCount,
      RoutinePrice: money(totals.totalWithoutDiscount / 100),
      BundlePrice: money(totals.total / 100),
      BundleSavings: money(totals.savings / 100),
      BundlePercentOff: percentOff,
      BoxID: this.$root?.dynamicBox?.boxId,
      BuilderURL: new URL(window.location.pathname, window.location.origin).href,
      CartURL: new URL("/cart", window.location.origin).href,
      $value: roundCents(totals.total / 100),
    };
  }

  handleCardRemove({ section, variantId }) {
    console.log("[Bundle Builder Cart Component]: Card Clicked", variantId);

    const $productCard = section.querySelector(
      `bundle-builder-product[variant-id="${variantId}"]`
    );

    console.log("product card", $productCard);

    if ($productCard) {
      $productCard.toggle();
    }
  }

  fetchState() {
    console.log("[Bundle Builder Cart Component]: State Refreshed");
    this.$sections = [...this.$root.$sections].map((section) => {
      section.selectionsWithProductVariants =
        this.getSectionSelectedProducts(section);
      return section;
    });
    this.cartItemsCount = this.$root.$sections.reduce(
      (acc, section) =>
        acc +
        section.selections.reduce(
          (acc, selection) => acc + selection.quantity,
          0
        ),
      0
    );

    // Validate: all sections must have 1 product AND total must meet box requirements
    const dynamicBox = this.$root.dynamicBox;
    if (dynamicBox) {
      const allSectionsValid = this.$root.$sections.every((section) => section.valid);
      // Use sizeInterval (exact sizes) or sizeRange (min/max)
      const requiredSize = dynamicBox.sizeInterval?.[0] || dynamicBox.sizeRange?.[0] || 3;
      const totalValid = this.cartItemsCount >= requiredSize;
      this.valid = allSectionsValid && totalValid;
    } else {
      this.valid = false;
    }

    if (this.valid && !this._trackedComplete) {
      this._trackedComplete = true;
      track("bundle_complete", this.trackingPayload());
    }
  }

  // Shared payload for bundle_complete and bundle_add_to_cart: value after the
  // Skio tier discount, list price, savings and the chosen product names.
  trackingPayload() {
    const totals = this.getTotals();
    const names = (this.$sections || []).flatMap((section) =>
      (section.selectionsWithProductVariants || []).map(
        ({ product, variant }) => product?.title || variant?.title || ""
      )
    );
    return {
      box_id: clip(this.$root?.dynamicBox?.boxId, 60),
      currency: currencyCode(),
      value: Math.round(totals.total) / 100,
      list_value: Math.round(totals.totalWithoutDiscount) / 100,
      savings: Math.round(totals.savings) / 100,
      item_count: this.cartItemsCount,
      item_names: clip(names.filter(Boolean).join(" | "), 100),
    };
  }

  getSectionSelectedProducts(section) {
    console.log("[Bundle Builder Cart Component]: getSectionSelectedProducts called", {
      hasBox: !!section.box,
      boxKeys: section.box ? Object.keys(section.box) : [],
      hasSelectableProductVariants: !!section.box?.selectableProductVariants,
      selectableProductVariantsType: typeof section.box?.selectableProductVariants,
      selectableProductVariantsLength: section.box?.selectableProductVariants?.length
    });

    if (!section.box || !section.box.selectableProductVariants) {
      console.warn("[Bundle Builder Cart Component]: No selectable variants in box data yet");
      return [];
    }

    console.log("[Bundle Builder Cart Component]: Processing selections", section.selections, "against", section.box.selectableProductVariants.length, "products");

    const results = [];

    for (const { variantId, quantity } of section.selections) {
      // Try to find product by variant platformId
      let product = section.box.selectableProductVariants.find((p) =>
        p.productVariants.some(
          (v) => v.platformId === `gid://shopify/ProductVariant/${variantId}`
        )
      );

      if (!product) {
        console.warn(
          `[Bundle Builder Cart Component]: Variant ${variantId} not found in Skio box.`
        );
        window.alert(
          `Product variant not found in Skio Dynamic Box. Please add this product/variant to the Dynamic Box in Skio: https://dashboard.skio.com/dynamic-box/edit/${this.$root.dynamicBox?.boxId}`
        );
        continue; // Skip this selection
      }

      const variant = product.productVariants.find(
        (v) => v.platformId === `gid://shopify/ProductVariant/${variantId}`
      );

      if (!variant) {
        console.warn(`[Bundle Builder Cart Component]: Variant ${variantId} not found in product variants`);
        continue;
      }

      results.push({
        variantId,
        product,
        variant,
        quantity,
      });
    }

    return results;
  }

  getTotals() {
    if (!this.$sections || !this.$root.dynamicBox) {
      return {
        total: 0,
        totalWithoutDiscount: 0,
        savings: 0,
        percentOff: 0,
      };
    }

    const dynamicBox = this.$root.dynamicBox;

    // Calculate total quantity across ALL sections
    const totalQuantity = this.$sections.reduce(
      (acc, section) =>
        acc + section.selections.reduce((a, s) => a + s.quantity, 0),
      0
    );

    // Find applicable discount tier from the single dynamic box
    const discountTiers = dynamicBox.percentDiscountTier || {};
    const currentTier = Object.keys(discountTiers)
      .sort((a, b) => +b - +a)
      .find((tier) => totalQuantity >= +tier);

    const tierDiscountPercentage = currentTier
      ? parseFloat(discountTiers[currentTier]) / 100
      : 0;

    // Calculate totals using unified discount across all sections
    let totalWithoutDiscount = 0;
    let totalWithDiscount = 0;

    this.$sections.forEach((section) => {
      if (!section.selectionsWithProductVariants) return;

      section.selectionsWithProductVariants.forEach((selection) => {
        if (!selection.variant) return;
        const price = selection.quantity * selection.variant.price * 100;
        totalWithoutDiscount += price;
        totalWithDiscount += price * (1 - tierDiscountPercentage);
      });
    });

    return {
      total: totalWithDiscount,
      totalWithoutDiscount: totalWithoutDiscount,
      savings: totalWithoutDiscount - totalWithDiscount,
      percentOff: Math.round(tierDiscountPercentage * 100),
    };
  }
}

if (!customElements.get("bundle-builder-cart")) {
  customElements.define("bundle-builder-cart", BundleBuilderCartComponent);
}

/*
    Custom Scrollbar Component
*/
export class CustomScrollbarComponent extends LitElement {
  static styles = [
    css`
      :host {
        display: block;
        position: relative;
      }

      .scrollbar-container {
        position: relative;
        overflow: hidden;
      }

      .scrollbar-content {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE/Edge */
        display: flex;
        gap: 1rem;
        flex-wrap: nowrap;
        white-space: nowrap;
        padding-bottom: 8px;
      }

      .scrollbar-content::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
      }

      ::slotted(*) {
        min-width: 180px;
        white-space: normal;
      }

      /* First card flush-left so it aligns with the section title / page header;
         last card keeps a right margin for scroll-end breathing room. */
      ::slotted(*:first-child) {
        margin-left: 0;
      }

      ::slotted(*:last-child) {
        margin-right: 20px;
      }

      @media screen and (min-width: 768px) {
        .scrollbar-content {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          align-items: stretch;
          gap: 16px;
          flex-wrap: wrap;
          white-space: wrap;
          overflow: hidden;
        }

        ::slotted(*) {
          min-width: 0;
        }

        .scrollbar-track {
          display: none;
        }

        /* Remove margins on desktop */
        ::slotted(*:first-child) {
          margin-left: 0;
        }

        ::slotted(*:last-child) {
          margin-right: 0;
        }
      }

      .scrollbar-track {
        position: relative;
        width: calc(100% - 40px);
        height: 4px;
        background-color: #e3d7cb;
        margin-top: 12px;
        margin-left: 20px;
        margin-right: 20px;
        border-radius: 2px;
        cursor: pointer;
      }

      .scrollbar-thumb {
        position: absolute;
        height: 100%;
        background-color: #8c8379;
        border-radius: 2px;
        transition: background-color 0.2s;
        cursor: grab;
      }

      .scrollbar-thumb:active {
        cursor: grabbing;
        background-color: #6d6560;
      }

      .scrollbar-thumb:hover {
        background-color: #6d6560;
      }
    `,
  ];

  static properties = {
    scrollWidth: {
      type: Number,
      state: true,
    },
    clientWidth: {
      type: Number,
      state: true,
    },
    scrollLeft: {
      type: Number,
      state: true,
    },
  };

  constructor() {
    super();
    this.scrollWidth = 0;
    this.clientWidth = 0;
    this.scrollLeft = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;
  }

  firstUpdated() {
    this.contentElement = this.shadowRoot.querySelector(".scrollbar-content");
    this.thumbElement = this.shadowRoot.querySelector(".scrollbar-thumb");
    this.trackElement = this.shadowRoot.querySelector(".scrollbar-track");

    this.updateScrollbar();
    
    // Listen to content scroll
    this.contentElement.addEventListener("scroll", () => {
      this.scrollLeft = this.contentElement.scrollLeft;
      this.updateThumbPosition();
    });

    // Observe size changes
    const resizeObserver = new ResizeObserver(() => {
      this.updateScrollbar();
    });
    resizeObserver.observe(this.contentElement);

    // Thumb drag handlers
    this.thumbElement.addEventListener("mousedown", this.handleThumbMouseDown.bind(this));
    document.addEventListener("mousemove", this.handleMouseMove.bind(this));
    document.addEventListener("mouseup", this.handleMouseUp.bind(this));

    // Track click handler
    this.trackElement.addEventListener("click", this.handleTrackClick.bind(this));

    // Touch support for mobile
    this.thumbElement.addEventListener("touchstart", this.handleThumbTouchStart.bind(this));
    document.addEventListener("touchmove", this.handleTouchMove.bind(this));
    document.addEventListener("touchend", this.handleTouchEnd.bind(this));
  }

  updateScrollbar() {
    if (!this.contentElement) return;
    
    this.scrollWidth = this.contentElement.scrollWidth;
    this.clientWidth = this.contentElement.clientWidth;
    this.scrollLeft = this.contentElement.scrollLeft;
    
    this.updateThumbPosition();
  }

  updateThumbPosition() {
    if (!this.thumbElement || !this.trackElement) return;
    
    const thumbWidth = this.getThumbWidth();
    const scrollPercentage = this.scrollLeft / (this.scrollWidth - this.clientWidth);
    const maxThumbPosition = this.trackElement.clientWidth - thumbWidth;
    const thumbPosition = scrollPercentage * maxThumbPosition;

    this.thumbElement.style.width = `${thumbWidth}px`;
    this.thumbElement.style.left = `${thumbPosition}px`;
  }

  getThumbWidth() {
    if (!this.trackElement) return 0;
    
    const ratio = this.clientWidth / this.scrollWidth;
    const thumbWidth = Math.max(ratio * this.trackElement.clientWidth, 30);
    return thumbWidth;
  }

  handleThumbMouseDown(e) {
    e.preventDefault();
    this.isDragging = true;
    this.startX = e.clientX;
    this.startScrollLeft = this.scrollLeft;
    this.thumbElement.style.cursor = 'grabbing';
  }

  handleThumbTouchStart(e) {
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.startScrollLeft = this.scrollLeft;
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    const deltaX = e.clientX - this.startX;
    const scrollRatio = this.scrollWidth / this.trackElement.clientWidth;
    const newScrollLeft = this.startScrollLeft + (deltaX * scrollRatio);
    
    this.contentElement.scrollLeft = Math.max(0, Math.min(newScrollLeft, this.scrollWidth - this.clientWidth));
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    const deltaX = e.touches[0].clientX - this.startX;
    const scrollRatio = this.scrollWidth / this.trackElement.clientWidth;
    const newScrollLeft = this.startScrollLeft + (deltaX * scrollRatio);
    
    this.contentElement.scrollLeft = Math.max(0, Math.min(newScrollLeft, this.scrollWidth - this.clientWidth));
  }

  handleMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.thumbElement.style.cursor = 'grab';
  }

  handleTouchEnd() {
    this.isDragging = false;
  }

  handleTrackClick(e) {
    if (e.target === this.thumbElement) return;
    
    const rect = this.trackElement.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const thumbWidth = this.getThumbWidth();
    const maxThumbPosition = this.trackElement.clientWidth - thumbWidth;
    const scrollPercentage = (clickX - thumbWidth / 2) / maxThumbPosition;
    
    this.contentElement.scrollLeft = scrollPercentage * (this.scrollWidth - this.clientWidth);
  }

  render() {
    return html`
      <div class="scrollbar-container">
        <div class="scrollbar-content" part="content">
          <slot></slot>
        </div>
        <div class="scrollbar-track">
          <div class="scrollbar-thumb"></div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("custom-scrollbar")) {
  customElements.define("custom-scrollbar", CustomScrollbarComponent);
}